
import { useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuthSession } from "@/hooks/useAuthSession";
import { useCurrentAffairsPerformance } from "@/hooks/useCurrentAffairsPerformance";

export interface PerformanceSummary {
  overallAccuracy: number;
  averageTimePerQuestion: number;
  topicsAnalyzed: number;
  recentImprovement: number;
  weakTopics: {
    topic: string;
    accuracy: number;
    attemptCount: number;
  }[];
  strongTopics: {
    topic: string;
    accuracy: number;
    attemptCount: number;
  }[];
  recommendedFocus: string[];
}

export function useCurrentAffairsPerformanceAnalysis() {
  const { userId } = useAuthSession();
  const [summary, setSummary] = useState<PerformanceSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data: performanceData, isLoading: performanceLoading } = useCurrentAffairsPerformance();

  const analyzePerformance = useCallback(async () => {
    if (!userId) {
      setError("User must be logged in");
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      // If we already have performance data from the hook, use it
      if (performanceData) {
        const weakTopics = performanceData.topicPerformance
          .filter(topic => topic.accuracy < 70)
          .map(topic => ({
            topic: topic.topic,
            accuracy: topic.accuracy,
            attemptCount: topic.questionsAttempted
          }))
          .sort((a, b) => a.accuracy - b.accuracy)
          .slice(0, 5);

        const strongTopics = performanceData.topicPerformance
          .filter(topic => topic.accuracy >= 70)
          .map(topic => ({
            topic: topic.topic,
            accuracy: topic.accuracy,
            attemptCount: topic.questionsAttempted
          }))
          .sort((a, b) => b.accuracy - a.accuracy)
          .slice(0, 5);

        const recommendedFocus = weakTopics.map(t => t.topic);

        const result: PerformanceSummary = {
          overallAccuracy: performanceData.accuracy,
          averageTimePerQuestion: performanceData.averageTimePerQuestion,
          topicsAnalyzed: performanceData.topicPerformance.length,
          recentImprovement: calculateRecentImprovement(performanceData.topicPerformance),
          weakTopics,
          strongTopics,
          recommendedFocus
        };

        setSummary(result);
        setLoading(false);
        return result;
      }

      // If not, fetch directly from the database
      const { data: quizData, error: quizError } = await supabase
        .from('current_affairs_quiz_results')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (quizError) throw quizError;

      if (!quizData || quizData.length === 0) {
        setLoading(false);
        return null;
      }

      // Calculate recent improvement (last quiz vs average of previous)
      const latestScore = quizData[0].percentage;
      const previousAvg = quizData.slice(1).reduce((sum, quiz) => sum + quiz.percentage, 0) / 
                         (quizData.length - 1 || 1);
      const recentImprovement = latestScore - previousAvg;

      // Fetch topics performance summary
      const { data: topicsData, error: topicsError } = await supabase
        .from('current_affairs_performance')
        .select('*')
        .eq('user_id', userId);

      if (topicsError) throw topicsError;

      const topicPerformance = (topicsData || []).map(record => ({
        topic: record.topic,
        accuracy: record.accuracy || (record.correct_count / Math.max(record.question_count, 1) * 100),
        attemptCount: record.question_count || 0
      }));

      const weakTopics = [...topicPerformance]
        .filter(topic => topic.accuracy < 70)
        .sort((a, b) => a.accuracy - b.accuracy)
        .slice(0, 5);

      const strongTopics = [...topicPerformance]
        .filter(topic => topic.accuracy >= 70)
        .sort((a, b) => b.accuracy - a.accuracy)
        .slice(0, 5);

      const recommendedFocus = weakTopics.map(t => t.topic);

      const result: PerformanceSummary = {
        overallAccuracy: quizData.reduce((sum, quiz) => sum + quiz.percentage, 0) / quizData.length,
        averageTimePerQuestion: calculateAverageTimeFromQuizzes(quizData),
        topicsAnalyzed: topicPerformance.length,
        recentImprovement,
        weakTopics,
        strongTopics,
        recommendedFocus
      };

      setSummary(result);
      return result;
    } catch (err) {
      console.error("Error analyzing current affairs performance:", err);
      setError("Failed to analyze performance data");
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId, performanceData]);

  return {
    summary,
    loading: loading || performanceLoading,
    error,
    analyzePerformance
  };
}

// Helper functions
function calculateRecentImprovement(topicPerformance: any[]): number {
  // Simple implementation - in a real app this would analyze performance trends over time
  return 0; // Placeholder
}

function calculateAverageTimeFromQuizzes(quizzes: any[]): number {
  let totalTime = 0;
  let totalQuestions = 0;
  
  quizzes.forEach(quiz => {
    if (quiz.time_taken && quiz.total_questions) {
      totalTime += quiz.time_taken;
      totalQuestions += quiz.total_questions;
    }
  });
  
  return totalQuestions > 0 ? totalTime / totalQuestions : 0;
}
